/* eslint-disable no-undef */
import { useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import { Button as AntButton, App, Card, Dropdown, MenuProps, Modal, Spin, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useNavigate, useParams } from 'react-router-dom';

import { ReactComponent as Plus } from '../../../../assets/add.svg';
import { ReactComponent as Ellipsis } from '../../../../assets/ellipsis.svg';
import { Button } from '../../../../custom';
import { deleteFitnessAthletics, getFitnessAndAthleticsByStudentLifeId } from '../../../../requests';
import { sanitizeAndLimitString } from '../../../../utils/sanitizeAndLimitString';
import DeleteModalContent from '../../../deleteModal/deleteModal';

import FitnessAndAthleticsForm from './fitnessAndAthleticsForm';

const FitnessAndAthletics = () => {
  const { notification } = App.useApp();
  const { id } = useParams();
  const [open, setOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [fitnessAthleticsItems, setFitnessAthleticsItems] = useState<FitnessAthletics>({} as FitnessAthletics);
  const [openDelete, setOpenDelete] = useState(false);
  // const [record, setRecord] = useState<FitnessAthletics>(
  //   {} as FitnessAthletics
  // );

  const navigate = useNavigate();

  const deleteFitnessAthleticsMutation = useMutation({
    mutationFn: deleteFitnessAthletics,
  });

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['get-fitness-athletics'],
    queryFn: () => getFitnessAndAthleticsByStudentLifeId(id!),
    enabled: !!id,
  });

  const deleteFitnessAndAthleticsHandler = async () => {
    try {
      await deleteFitnessAthleticsMutation.mutateAsync(fitnessAthleticsItems?.id, {
        onSuccess: (data) => {
          notification.success({
            message: 'Success',
            description: data?.message,
          });
          refetch();
          setOpenDelete((prevState) => !prevState);
        },
      });
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error?.response?.data?.message,
      });
    }
  };

  const columns: ColumnsType<FitnessAthletics> = [
    {
      key: 'title',
      title: 'Title',
      dataIndex: 'title',
    },
    {
      key: 'description',
      title: 'Description',
      dataIndex: 'description',
      render: (_: any, { description }: any) => {
        const limitedCleanHtml = sanitizeAndLimitString(description);
        return <div dangerouslySetInnerHTML={{ __html: limitedCleanHtml }} />;
      },
    },
    {
      key: 'status',
      title: 'Status',
      dataIndex: 'activeStatus',
      render: (_, { activeStatus }) => (activeStatus ? 'Active' : 'Inactive'),
    },
    {
      key: 'action',
      title: '',
      render: (_, record) => {
        const items: MenuProps['items'] = [
          {
            key: '1',
            label: 'Add items',
            onClick: () => {
              navigate(`/student-life/${record.id}/fitness-athletics-item`);
            },
          },
          {
            key: '2',
            label: 'Add Images',
            onClick: () => {
              navigate(`/student-life/${record.id}/fitness-athletics-image`);
            },
          },
          {
            key: '3',
            label: 'Edit',
            onClick: () => {
              setFitnessAthleticsItems(record);
              setOpenEdit(true);
            },
          },
          {
            key: '4',
            label: 'Delete',
            onClick: () => {
              setFitnessAthleticsItems(record);
              setOpenDelete(true);
            },
          },
        ];

        return (
          <Dropdown menu={{ items }} trigger={['click']}>
            <AntButton type="text" icon={<Ellipsis />} />
          </Dropdown>
        );
      },
    },
  ];

  const fitnessAthleticsData = data?.data as FitnessAthletics[];

  if (isLoading) {
    return <Spin />;
  }

  if (isError) {
    return <div>Error: {error?.message}</div>;
  }

  return (
    <div>
      <section className="space-between">
        <h3>Student Life: Fitness and Athletics Setup</h3>

        {fitnessAthleticsData?.length === 0 && (
          <Button onClick={() => setOpen(true)} iconBefore={<Plus />} text="Setup" />
        )}
      </section>
      <br />
      <Card bordered={false}>
        <Table
          dataSource={fitnessAthleticsData}
          columns={columns}
          pagination={{ position: ['bottomCenter'] }}
          rowKey={(record) => record.id}
          scroll={{ x: true }}
        />
      </Card>

      <Modal open={open} onCancel={() => setOpen(false)} centered title="Create Fitness and Athletics" footer={null}>
        <FitnessAndAthleticsForm item={fitnessAthleticsItems} handleClose={() => setOpen(false)} />
      </Modal>

      <Modal
        open={openEdit}
        onCancel={() => setOpenEdit(false)}
        centered
        title="Edit Fitness and Athletics"
        footer={null}
      >
        <FitnessAndAthleticsForm item={fitnessAthleticsItems} handleClose={() => setOpenEdit(false)} />
      </Modal>

      <Modal
        open={openDelete}
        onCancel={() => setOpenDelete(false)}
        centered
        title="Delete Fitness And Athletics"
        footer={null}
      >
        <DeleteModalContent
          isLoading={deleteFitnessAthleticsMutation?.isPending}
          handleCloseModal={() => setOpenDelete(false)}
          handleSubmit={deleteFitnessAndAthleticsHandler}
          title={fitnessAthleticsItems?.title}
        />
      </Modal>
    </div>
  );
};

export default FitnessAndAthletics;
